import { useEffect, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { Booking } from '../../models/Booking'
import { Invoice } from '../../models/Invoice'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { ComponentData } from '../../utils/typing'
import { upgradeUserAccount } from '../../utils/users'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import { Chip } from '../common/fields/Chip'
import { GuardrailModal } from '../common/modals/GuardrailModal'
import { WorkflowPage } from '../workflows/WorkflowPage'

const viewWillAppear = async (base: PocketComponent) => {
    return {
        industries: await RecordFactory.getInstance().loadIndustries(),
    }
}

const render = ({ data, base }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const [user, setUser] = useState(base.me.clone())
    const navigate = usePSNavigate()
    const [isSaving, setIsSaving] = useState(false)

    const [modalState, setModalState] = useState<
        'upgrade_modal' | 'disable_reminders_modal' | undefined
    >(undefined)

    const channelOptions = Booking.channelStrings()
        .filter((option) => !!option.id)
        .map((channel) => ({
            id: channel.id!,
            title: channel.label!,
        }))

    const hasEdits =
        user.channel !== base.me.channel ||
        user.reminder !== base.me.reminder ||
        user.invoiceReminder !== base.me.invoiceReminder ||
        user.silenceTransactions !== base.me.silenceTransactions

    function closeModal() {
        setModalState(undefined)
    }

    function cancelTurnOffReminders() {
        setUser(user.clone({ invoice_reminder: '30' }))
        closeModal()
    }

    const submitChanges = async () => {
        if (user.solo || user.company || user.partner) {
            setIsSaving(true)
            const result = await RecordFactory.getInstance().sendUser(user)
            setIsSaving(false)
            if (!result.ok) {
                base.logAndDisplayError(result.error)
                return
            }

            navigate('/settings')
        }
        else {
            setModalState('upgrade_modal')
        }
    }

    function upgradeAccount() {
        upgradeUserAccount(base, user, usePSNavigate)
    }

    useEffect(() => {
        if (user.invoiceReminder === '0') {
            setModalState('disable_reminders_modal')
        }
    }, [user.invoiceReminder])

    return (
        <WorkflowPage
            title={user.payee || user.client ? 'Upgrade to business' : 'Notifications'}
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button
                        intent="primary"
                        disabled={!hasEdits}
                        onClick={submitChanges}
                        isLoading={isSaving}
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start space-y-6">
                <WorkflowPage.Card>
                    <WorkflowPage.IconSection
                        title="Send via"
                        icon={'messageArrowUp.large'}
                        description="Choose how you generally prefer to notify clients about
                                    appointments or invoices."
                    >
                        <div className="flex flex-wrap gap-2">
                            {channelOptions.map((option) => (
                                <Chip
                                    key={`cc-option-${option.id}`}
                                    title={option.title}
                                    isSelected={user.channel === option.id}
                                    onClick={() => setUser(user.clone({ channel: option.id }))}
                                    size="small"
                                />
                            ))}
                        </div>
                    </WorkflowPage.IconSection>
                    <WorkflowPage.IconSection
                        title="Booking reminders"
                        icon={'calendarAlarmClock.large'}
                        description="Choose if and when you’d like your client to receive a reminder ahead of their appointment."
                    >
                        <div className="flex flex-wrap gap-2">
                            {Booking.reminderStrings().map((option) => (
                                <Chip
                                    key={`cc-option-${option.id}`}
                                    title={option.label ?? ''}
                                    isSelected={user.reminder === option.id}
                                    onClick={() => setUser(user.clone({ reminder: option.id }))}
                                    size="small"
                                />
                            ))}
                        </div>
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Payment reminders"
                        icon={'moneyAlarmClock.large'}
                        description="Choose how often your client will be reminded to pay invoices
                                    and packages."
                    >
                        <div className="flex flex-wrap gap-2">
                            {Invoice.reminderStrings().map((option) => (
                                <Chip
                                    key={`cc-option-${option.id}`}
                                    title={option.label ?? ''}
                                    isSelected={user.invoiceReminder === option.id}
                                    onClick={() =>
                                        setUser(user.clone({ invoice_reminder: option.id }))
                                    }
                                    size="small"
                                />
                            ))}
                        </div>
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Silence your transactions"
                        icon={'bellOff.large'}
                        description="Reduce the noise level in your account by silencing all
                                    transaction notifications."
                    >
                        <div className="flex flex-wrap gap-2">
                            {User.silenceTransactionStrings().map((option) => (
                                <Chip
                                    key={`cc-option-${option.id}`}
                                    title={option.label ?? ''}
                                    isSelected={user.silenceTransactions === option.id}
                                    onClick={() =>
                                        setUser(user.clone({ silence_transactions: option.id }))
                                    }
                                    size="small"
                                />
                            ))}
                        </div>
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>
            {modalState === 'upgrade_modal' && (
                <GuardrailModal
                    title="Upgrade to business account"
                    buttonText="Upgrade to business account"
                    onClose={closeModal}
                    onAction={upgradeAccount}
                >
                    <Paragraph>Would you like to upgrade to a business account?</Paragraph>
                </GuardrailModal>
            )}
            {modalState === 'disable_reminders_modal' && (
                <GuardrailModal
                    title="Turn off all payment reminders?"
                    buttonText="Confirm"
                    dismissButtonText="Cancel"
                    onClose={cancelTurnOffReminders}
                    onAction={closeModal}
                >
                    Turning off payment reminders may result in lost income for delinquent payments,
                    are you sure you want to turn off all payment reminders?
                </GuardrailModal>
            )}

            <NavigationBlockerPrompt when={hasEdits && !isSaving} />
        </WorkflowPage>
    )
}
export const SetupNotificationsPage = usePSComponent(render, viewWillAppear)
